import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { useNoticeBannersStackContext } from '@sweetgreen/garnish';

import { LoadingAnimation } from '@order/components';
import type { SubscriptionCreditCard } from '@order/graphql';
import {
  TierName,
  useGetPaymentSubscriptionProfileQuery,
  useSubscribeMutation,
} from '@order/graphql';
import { useSweetpassTier } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';
import { getUrqlError } from '@order/utils';

import { TierUpgradeCelebrationModal } from '../../components/TierUpgradeCelebrationModal';
import { useNavigateToMainTabs } from '../../navigation';
import type { SweetpassCheckoutScreenProps } from '../../navigation/AppNavigation.props';
import { usePaymentMethodToken } from '../SweetpassPaymentMethodAddScreen';
import { useSavedPaymentMethod } from './hooks';
import { SweetpassCheckoutScreenContent } from './SweetpassCheckoutScreenContent';
import { useSweetpassCheckoutLoadedContent } from './useSweetpassCheckoutLoadedContent';

// ────────────────────────────────────────────────────────────────────────────────

export const SweetpassCheckoutScreen = ({
  navigation,
  route,
}: SweetpassCheckoutScreenProps) => {
  const { planId } = route.params ?? {};
  const [hasCustomerBeenUpgraded, setHasCustomerBeenUpgraded] = useState(false);
  const { isSweetpassEnabled, isSweetpassTierLoading, sweetpassTierName } =
    useSweetpassTier('cache-and-network');
  const { t } = useLocalizationContext();
  const { track } = useTelemetry();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const navigateToMainTabs = useNavigateToMainTabs();

  const { content: loadedContent, isLoading: isContentLoading } =
    useSweetpassCheckoutLoadedContent();

  const [, subscribeMutation] = useSubscribeMutation();
  const { getPaymentMethodToken, removePaymentMethodToken } =
    usePaymentMethodToken();
  const { savedPaymentMethod, setSavedPaymentMethod } = useSavedPaymentMethod();

  const [paymentSubscriptionProfileResponse] =
    useGetPaymentSubscriptionProfileQuery({
      requestPolicy: 'cache-and-network',
    });

  // ─── Derived Data ────────────────────────────────────────────────────

  const paymentSubscriptionProfile =
    paymentSubscriptionProfileResponse.data?.paymentSubscriptionProfile;

  const isPaymentSubscriptionProfileQueryResolved =
    paymentSubscriptionProfileResponse !== undefined;

  const isLoading =
    isSweetpassTierLoading ||
    isContentLoading ||
    !isPaymentSubscriptionProfileQueryResolved;

  const hasContent = isSweetpassEnabled && Boolean(loadedContent);

  const isSweetpassEnabledForCustomer =
    !isSweetpassTierLoading && isSweetpassEnabled;
  const isSweetpassPlusMember = sweetpassTierName === TierName.SweetpassPlus;

  const navigateToSweetpassHome = useCallback(() => {
    // Workaround for the web-specific issue where navigating to MainTabs from a modal
    // results in an incorrect address bar URL.
    navigation.getParent()?.goBack();
    void navigateToMainTabs('LoyaltyTab', {
      screen: 'SweetpassHome',
    });
  }, [navigateToMainTabs, navigation]);

  const navigateToHome = useCallback(() => {
    void navigateToMainTabs('HomeTab', {
      screen: 'Home',
    });
  }, [navigateToMainTabs]);

  // ─────────────────────────────────────────────────────────────────

  const handleCta = useCallback(async () => {
    const token = await getPaymentMethodToken();

    await removePaymentMethodToken();

    const response = await subscribeMutation({
      planId,
      token,
      paymentId: token ? null : savedPaymentMethod,
    });

    const createdSubscription = response?.data?.createSubscription;

    if (!createdSubscription) {
      addNoticeBanner({
        id: 'sweetpass-subscription-error',
        text: t('sweetpass.checkout.join-sweetpass-plus.notice.failure'),
        palette: 'caution',
      });
      track('sweetpass_account.upgrade.failure', {
        systemError: getUrqlError(response?.error) ?? t('general.error'),
      });

      return;
    }

    track('sweetpass_account.upgrade.success');
    track('sweetpass.tier.changed', { loyaltyTier: TierName.SweetpassPlus });

    setHasCustomerBeenUpgraded(true);
  }, [
    t,
    addNoticeBanner,
    getPaymentMethodToken,
    removePaymentMethodToken,
    subscribeMutation,
    planId,
    savedPaymentMethod,
    track,
  ]);

  // ─── Redirects ───────────────────────────────────────────────────────

  // NOTE: Redirect conditions are presented as an array,
  //       making it easy to check several conditions.

  const redirectToSweetpassHomeScreenConditions =
    useMemo<ScreenRedirectConditions>(
      () => [
        !hasCustomerBeenUpgraded &&
          isSweetpassEnabledForCustomer &&
          isSweetpassPlusMember,
      ],
      [
        hasCustomerBeenUpgraded,
        isSweetpassEnabledForCustomer,
        isSweetpassPlusMember,
      ],
    );
  const redirectToSweetpassUpgradeScreenConditions =
    useMemo<ScreenRedirectConditions>(
      () => [isSweetpassEnabledForCustomer && !planId],
      [isSweetpassEnabledForCustomer, planId],
    );
  const redirectToHomeScreenConditions = useMemo<ScreenRedirectConditions>(
    () => [!isSweetpassEnabledForCustomer, !isLoading && !hasContent],
    [isSweetpassEnabledForCustomer, isLoading, hasContent],
  );

  /**
   * Redirects customer to the Sweetpass Home screen, if any of the following conditions are met.
   *
   * - The current customer has Sweetpass+ subscription (already upgraded)
   */
  useLayoutEffect(() => {
    const shouldRedirect =
      redirectToSweetpassHomeScreenConditions.some(Boolean);

    if (!shouldRedirect) return;

    void navigateToMainTabs('LoyaltyTab', { screen: 'SweetpassHome' });
  }, [navigateToMainTabs, navigation, redirectToSweetpassHomeScreenConditions]);

  /**
   * Redirects customer to the Home Screen, if any of the following conditions are met.
   *
   * - The current customer does not have Sweetpass enabled.
   */
  useLayoutEffect(() => {
    const shouldRedirect = redirectToHomeScreenConditions.some(Boolean);

    if (!shouldRedirect) return;

    void navigateToMainTabs('HomeTab', { screen: 'Home' });
  }, [navigateToMainTabs, navigation, redirectToHomeScreenConditions]);

  /**
   * Redirects customer to the Sweetpass Upgrade screen, if the appropriate plan ID is not available.
   */
  useLayoutEffect(() => {
    const shouldRedirect =
      redirectToSweetpassUpgradeScreenConditions.some(Boolean);

    if (!shouldRedirect) return;

    navigation.replace('SweetpassUpgrade');
  }, [navigation, redirectToSweetpassUpgradeScreenConditions]);

  // ─────────────────────────────────────────────────────────────────────

  if (isLoading) {
    return <LoadingAnimation />;
  }

  if (!isSweetpassEnabled || !loadedContent || !paymentSubscriptionProfile) {
    return null;
  }

  return (
    <>
      <SweetpassCheckoutScreenContent
        testID="sweetpass-checkout-screen"
        content={loadedContent}
        creditCards={
          (paymentSubscriptionProfile.creditCards as readonly SubscriptionCreditCard[]) ??
          []
        }
        savedCardHandler={setSavedPaymentMethod}
        ctaHandler={handleCta}
      />
      <TierUpgradeCelebrationModal
        testID="sweetpass-checkout-celebration-modal"
        onDismiss={navigateToHome}
        onExplore={navigateToSweetpassHome}
        visible={hasCustomerBeenUpgraded}
        headingText={t('sweetpass-celebration.title')}
        footerText={t('sweetpass-celebration.subtitle')}
        btnText={t('sweetpass-celebration.button-text')}
      />
    </>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ScreenRedirectConditions = readonly boolean[];
